import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { PlugIcon, CodeIcon, SendIcon, SearchIcon, BarChart3Icon, CalendarCheckIcon } from 'lucide-react';
// The capabilities the headline cycles through — each is something credits
// power Giga to do, paired with a lucide glyph shown in a Giga-orange rounded
// square so every item reads as a branded lockup (no third-party purple).
const CAPABILITIES: {
  label: string;
  icon: ComponentType<{
    size?: number;
    strokeWidth?: number;
    className?: string;
  }>;
}[] = [{
  label: 'Integrations',
  icon: PlugIcon
}, {
  label: 'Code',
  icon: CodeIcon
}, {
  label: 'Outreach',
  icon: SendIcon
}, {
  label: 'Research',
  icon: SearchIcon
}, {
  label: 'Reports',
  icon: BarChart3Icon
}, {
  label: 'Scheduling',
  icon: CalendarCheckIcon
}];

// The animated trailing word in the headline: "Giga does: ___". Cycles through
// CAPABILITIES on a calm timer, sliding + blurring each in/out. Width is held
// stable by an invisible sizer stack so the centered headline never reflows.
// Respects reduced-motion by rendering the first item statically.
function RotatingCapability() {
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % CAPABILITIES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduce]);
  const active = CAPABILITIES[index];
  const ActiveIcon = active.icon;
  return <span className="relative inline-grid place-items-start align-baseline" data-id="element-3879" data-mp="20f7rsj21bhsm">
      {/* Invisible sizer — reserves the width of the widest capability so the
          centered headline never shifts as words swap. */}
      <span className="invisible col-start-1 row-start-1 grid" aria-hidden="true" data-id="element-3880" data-mp="1cnk92oexfd1f">
        
        {CAPABILITIES.map(c => {
        const Icon = c.icon;
        return <span key={c.label} className="col-start-1 row-start-1 inline-flex items-center gap-3 whitespace-nowrap" data-id="element-3881" data-mp="1hlu6z6s1crqq">
              
              <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] w-[1em] h-[1em]" data-id="element-3882" data-mp="3el2y35i87o71">
                <Icon size={1} data-id="element-3883" data-mp="39lhwenbo91qm" />
              </span>
              {c.label}
            </span>;
      })}
      </span>
      {/* Visible animated layer, absolutely overlaid on the sizer. */}
      <span className="col-start-1 row-start-1 block relative" data-id="element-3884" data-mp="65aw8qsgtmml">
        <AnimatePresence initial={false} mode="wait" data-id="element-3885" data-mp="64hte0wmnh9q">
          <motion.span key={active.label} initial={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '0.35em',
          filter: 'blur(6px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          y: '-0.35em',
          filter: 'blur(6px)'
        }} transition={{
          duration: 0.45,
          ease: [0.16, 1, 0.3, 1]
        }} className="inline-flex items-center gap-3 whitespace-nowrap" data-id="element-3886" data-mp="15y7g0i1a5hd">
            
            <span className="inline-flex items-center justify-center rounded-2xl bg-[#EA580C] text-white w-[1.15em] h-[1.15em] shadow-sm" data-id="element-3887" data-mp="xcsvgnf1lf7z">
              <ActiveIcon size={undefined} className="w-[0.62em] h-[0.62em]" strokeWidth={2.5} data-id="element-3888" data-mp="11dbnoikuknap" />
              
            </span>
            <span data-id="element-3889" data-mp="166e9g4jdusgc">{active.label}</span>
          </motion.span>
        </AnimatePresence>
      </span>
    </span>;
}
function GigaAvatar({
  size = 24
}: {
  size?: number;
}) {
  return <span className="flex shrink-0 items-center justify-center rounded-full bg-[#EA580C]" style={{
    width: size,
    height: size
  }} data-id="element-3890" data-mp="1g1zfcdrkocvd">
      
      <span className="bg-white" style={{
      width: size * 0.56,
      height: size * 0.56,
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-3891" data-mp="1p3yxj9ebpmzw" />
      
    </span>;
}
export function PricingCreditsUseCases() {
  return <section className="py-20 md:py-32 bg-[#FAF8F4] overflow-hidden" data-id="element-3892" data-mp="35itfgkg3b8c8">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3893" data-mp="30la01h7nopip">
        <div className="text-center mb-16 md:mb-20" data-id="element-3894" data-mp="2cunsrs5h799r">
          <h2 className="text-4xl md:text-6xl font-title font-bold text-[#2B211A] tracking-tight leading-[1.1]" data-id="element-3895" data-mp="2cvfl8u3s0qvt">
            <span className="block" data-id="element-3896" data-mp="27xe44epwv1mz">Credits power everything</span>
            <span className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-1" data-id="element-3897" data-mp="2mlipxrk9ft9z">
              Giga does:
              <RotatingCapability data-id="element-3898" data-mp="2hsfu1cs0g7qm" />
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3899" data-mp="1u2v8qn8dwye6">
          {/* Card 1 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col" data-id="element-3900" data-mp="1p4k0ggxqmvy2">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3901" data-mp="9dr6yg8tfk21">
              Quick Tasks
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3902" data-mp="3rs0cmsim5upy">
              100-300 credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3903" data-mp="3vudkbmgx82pd">
              Slack summary + CRM follow-up
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3904" data-mp="nebqjemk1jwl">
              <div className="flex gap-3" data-id="element-3905" data-mp="sd5231ia70u4">
                <div className="w-8 h-8 rounded-full bg-[#E7DFD5] flex items-center justify-center text-[#6B5D52] font-bold text-xs shrink-0" data-id="element-3906" data-mp="sbu0n8cb6566">
                  S
                </div>
                <div data-id="element-3907" data-mp="jadqn1peo52h">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3908" data-mp="je1k60uuorh7">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3909" data-mp="2z2a02nwmr0vy">
                      Sarah
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3910" data-mp="34035heq3zqv6">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3911" data-mp="19o1y8icefseb">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3912" data-mp="zvmhtpgr3bvj">@Giga</span>{' '}
                    summarize yesterday's #sales thread and flag anything I need
                    to follow up on
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3913" data-mp="qtdbzcwfg6oh">
                <div className="mt-1" data-id="element-3914" data-mp="lw3m5w54lt04">
                  <GigaAvatar size={32} data-id="element-3915" data-mp="lv20kxqhho45" />
                </div>
                <div data-id="element-3916" data-mp="gwiemxbw4cbs">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3917" data-mp="gxscpmiqtkif">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3918" data-mp="cwgjz84koj60">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3919" data-mp="2barnn2c6hqjd">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3920" data-mp="3p84e84e4c684">9:15 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3921" data-mp="3u5mel6e2ld4i">
                    3 deals discussed. Two on track — Acme closes Friday, Bloom
                    signed. One needs you: DataSync went silent after the demo.
                    I drafted a follow-up in HubSpot, want me to send it?
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col" data-id="element-3922" data-mp="3f8n9bp6oxwd7">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3923" data-mp="3k9t2tp86dnns">
              Complex Workflows
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3924" data-mp="ulac1o7u2ix1">
              500-1,500 credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3925" data-mp="b574ub8tniet">
              Website change → ready to review
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3926" data-mp="67f3kqmaejxy">
              <div className="flex gap-3" data-id="element-3927" data-mp="jxqz6zi2z0tu">
                <div className="w-8 h-8 rounded-full bg-[#D5E7E6] flex items-center justify-center text-[#526B6A] font-bold text-xs shrink-0" data-id="element-3928" data-mp="jyk8o88fc4fo">
                  M
                </div>
                <div data-id="element-3929" data-mp="tv5dotkg1fji">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3930" data-mp="tudhphmxpfsp">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3931" data-mp="oxderfldst5t">
                      Mike
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3932" data-mp="12w38cl8zsapf">11:32 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3933" data-mp="yvjcrm42hf5e">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3934" data-mp="34pt0a1vkrj5e">@Giga</span>{' '}
                    the pricing page still says "$99/mo" on the starter tier. It
                    should be "$79/mo". Can you update it?
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3935" data-mp="2um5k5pimzra8">
                <div className="mt-1" data-id="element-3936" data-mp="2gtg0vsuht0gt">
                  <GigaAvatar size={32} data-id="element-3937" data-mp="2gsec6lkk0fgm" />
                </div>
                <div data-id="element-3938" data-mp="2lqxywo6z1va1">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3939" data-mp="2lq6em9itrjz2">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3940" data-mp="26u79n7r0hzy5">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3941" data-mp="2bv4pvesgojx0">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3942" data-mp="22s3jre3listc">11:38 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3943" data-mp="3s7jrqq5sa8hn">
                    Done ✅ Updated the pricing page — Starter tier now shows
                    $79/mo. Preview is ready for your review:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline" data-id="element-3944" data-mp="3p1m4n6raj2do">
                      acme.com/pricing-preview
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 */}
          <div className="bg-gradient-to-b from-white to-[#FAF8F4] border border-neutral-200 rounded-[2rem] p-8 shadow-sm flex flex-col md:col-span-2 lg:col-span-1" data-id="element-3945" data-mp="3f9zw5i6c7hem">
            <span className="text-[#EA580C] font-semibold text-sm mb-2" data-id="element-3946" data-mp="bu2k13om7zdn">
              Full Projects
            </span>
            <h3 className="text-3xl font-title font-bold text-[#2B211A] mb-2" data-id="element-3947" data-mp="3u0pagtf3hcmp">
              2,000+ credits
            </h3>
            <p className="text-neutral-500 font-medium mb-8" data-id="element-3948" data-mp="2widpgbwbcw6">
              Deep research + presentation
            </p>

            <div className="flex-1 flex flex-col gap-4" data-id="element-3949" data-mp="2nlfxcjmwz2z4">
              <div className="flex gap-3" data-id="element-3950" data-mp="2nitqynbretih">
                <div className="w-8 h-8 rounded-full bg-[#E7D5E4] flex items-center justify-center text-[#6B5268] font-bold text-xs shrink-0" data-id="element-3951" data-mp="2f3cva6cwjfcu">
                  L
                </div>
                <div data-id="element-3952" data-mp="2p1trbhqp6k5r">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3953" data-mp="2p2by38azkgrx">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3954" data-mp="2k2z8wgwn6xm6">
                      Lisa
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3955" data-mp="1wcmviqimo8o7">9:14 AM</span>
                  </div>
                  <div className="bg-white border border-neutral-100 rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3956" data-mp="1rf36cvke6bhh">
                    <span className="text-[#EA580C] font-semibold" data-id="element-3957" data-mp="264irnw15lops">@Giga</span>{' '}
                    we need a competitive analysis — us vs Notion AI, Glean, and
                    Moveworks. Pricing, features, and positioning.
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3958" data-mp="18nakdl3qpqj9">
                <div className="mt-1" data-id="element-3959" data-mp="38qbglsydueft">
                  <GigaAvatar size={32} data-id="element-3960" data-mp="38pa7hu0xg20q" />
                </div>
                <div data-id="element-3961" data-mp="3dowgtsdmwy49">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-3962" data-mp="3do4n6n3llagm">
                    <span className="font-semibold text-sm text-[#2B211A]" data-id="element-3963" data-mp="3ig4u4zi353mp">
                      Giga
                    </span>
                    <span className="text-[10px] font-bold bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded" data-id="element-3964" data-mp="3mgex03akzdxc">
                      APP
                    </span>
                    <span className="text-xs text-neutral-400" data-id="element-3965" data-mp="2uq0pcot5uxu4">9:45 AM</span>
                  </div>
                  <div className="bg-[#FCF3EC] border border-[#FBEAE0] rounded-2xl rounded-tl-sm p-3 text-sm text-[#2B211A] shadow-sm" data-id="element-3966" data-mp="2ys5htk7vhof6">
                    I've compiled the research into a 12-slide deck. I analyzed
                    their public pricing, recent feature releases, and G2
                    reviews. Here's the draft:{' '}
                    <a href="#" className="text-[#EA580C] hover:underline font-medium" data-id="element-3967" data-mp="izg89fw731cj">
                      
                      Competitive_Analysis_Q3.pdf
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}